import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import s from "../clinic.module.css";

function menuStyleFromAnchor(anchor) {
  if (!anchor || typeof anchor.getBoundingClientRect !== "function") {
    return {
      top: 80,
      right: 24,
    };
  }
  const rect = anchor.getBoundingClientRect();
  const gap = 6;
  const menuWidth = 200;
  let top = rect.bottom + gap;
  let right = Math.max(8, window.innerWidth - rect.right);
  if (top + 160 > window.innerHeight) {
    top = Math.max(8, rect.top - gap - 120);
  }
  if (right + menuWidth > window.innerWidth) {
    right = 8;
  }
  return { top, right };
}

export default function ClubLifePostActionSheet({
  post,
  open,
  busy = false,
  anchor = null,
  onClose,
  onEdit,
  onDelete,
}) {
  const isMobile = useIsMobile(768);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const menuRef = useRef(null);

  useEffect(() => {
    if (open) setConfirmDelete(false);
  }, [open, post?.id]);

  useLayoutEffect(() => {
    if (!open || isMobile) return undefined;
    const update = () => setPos(menuStyleFromAnchor(anchor));
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, isMobile, anchor, post?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    if (isMobile) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, busy, onClose, isMobile]);

  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onPointer = (e) => {
      if (busy) return;
      const menu = menuRef.current;
      if (menu?.contains(e.target)) return;
      if (anchor && typeof anchor.contains === "function" && anchor.contains(e.target)) {
        return;
      }
      onClose?.();
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open, isMobile, busy, onClose, anchor]);

  if (!open || !post) return null;

  const handleDelete = () => {
    if (confirmDelete) {
      onDelete?.(post);
      return;
    }
    setConfirmDelete(true);
  };

  if (!isMobile) {
    return createPortal(
      <div
        ref={menuRef}
        className={s.postActionPopover}
        style={{ top: pos.top, right: pos.right }}
        role="menu"
        aria-label="Действия с записью"
      >
        {confirmDelete ? (
          <>
            <p className={s.postActionPopoverWarn}>
              Удалить запись? Это нельзя отменить.
            </p>
            <button
              type="button"
              role="menuitem"
              className={`${s.postActionPopoverItem} ${s.postActionPopoverItemDanger}`}
              onClick={handleDelete}
              disabled={busy}
            >
              {busy ? "Удаляем…" : "Удалить запись"}
            </button>
            <button
              type="button"
              role="menuitem"
              className={s.postActionPopoverItem}
              onClick={() => setConfirmDelete(false)}
              disabled={busy}
            >
              Назад
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              role="menuitem"
              className={s.postActionPopoverItem}
              onClick={() => onEdit?.(post)}
              disabled={busy}
            >
              Редактировать
            </button>
            <button
              type="button"
              role="menuitem"
              className={`${s.postActionPopoverItem} ${s.postActionPopoverItemDanger}`}
              onClick={handleDelete}
              disabled={busy}
            >
              Удалить
            </button>
          </>
        )}
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className={s.postActionSheetOverlay}
      role="dialog"
      aria-modal="true"
      onClick={() => !busy && onClose?.()}
    >
      <div
        className={s.postActionSheet}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.postActionSheetHandle} aria-hidden />

        {confirmDelete ? (
          <>
            <p className={s.postActionSheetWarn}>
              Удалить запись? Это действие нельзя отменить.
            </p>
            <button
              type="button"
              className={`${s.postActionSheetBtn} ${s.postActionSheetBtnDanger}`}
              onClick={handleDelete}
              disabled={busy}
            >
              {busy ? "Удаляем…" : "Удалить запись"}
            </button>
            <button
              type="button"
              className={s.postActionSheetBtn}
              onClick={() => setConfirmDelete(false)}
              disabled={busy}
            >
              Назад
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={s.postActionSheetBtn}
              onClick={() => onEdit?.(post)}
              disabled={busy}
            >
              Редактировать
            </button>
            <button
              type="button"
              className={`${s.postActionSheetBtn} ${s.postActionSheetBtnDanger}`}
              onClick={handleDelete}
              disabled={busy}
            >
              Удалить
            </button>
          </>
        )}

        <button
          type="button"
          className={`${s.postActionSheetBtn} ${s.postActionSheetBtnCancel}`}
          onClick={onClose}
          disabled={busy}
        >
          Отмена
        </button>
      </div>
    </div>,
    document.body,
  );
}
